import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Stack,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useConfirm } from '../../contexts/ConfirmDialogContext';

interface ProformaInvoice {
  id: number;
  invoice_number: string;
  client_name: string;
  case_subject: string;
  amount: number;
  currency: string;
  status: string;
  issue_date: string;
  due_date: string;
  description: string;
}

const sampleData: ProformaInvoice[] = [
  { id: 1, invoice_number: 'PRF-2026-001', client_name: 'Acme Corp', case_subject: 'Contract Review', amount: 5000, currency: 'USD', status: 'draft', issue_date: '2026-02-01', due_date: '2026-03-01', description: 'Legal consultation for contract review' },
  { id: 2, invoice_number: 'PRF-2026-002', client_name: 'Global Industries', case_subject: 'Patent Filing', amount: 12000, currency: 'EUR', status: 'sent', issue_date: '2026-02-10', due_date: '2026-03-10', description: 'Patent filing and prosecution services' },
  { id: 3, invoice_number: 'PRF-2026-003', client_name: 'Tech Solutions', case_subject: 'Employment Dispute', amount: 8500, currency: 'USD', status: 'approved', issue_date: '2026-02-15', due_date: '2026-03-15', description: 'Representation in employment dispute' },
  { id: 4, invoice_number: 'PRF-2026-004', client_name: 'Smith & Co', case_subject: 'Merger Advisory', amount: 25000, currency: 'USD', status: 'draft', issue_date: '2026-02-20', due_date: '2026-03-20', description: 'M&A legal advisory services' },
];

interface FormData {
  client_name: string;
  case_subject: string;
  amount: string;
  currency: string;
  issue_date: string;
  due_date: string;
  description: string;
}

const initialFormData: FormData = {
  client_name: '',
  case_subject: '',
  amount: '',
  currency: 'USD',
  issue_date: '',
  due_date: '',
  description: '',
};

const Proforma: React.FC = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [rows, setRows] = useState<ProformaInvoice[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProformaInvoice | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRows = searchQuery.trim()
    ? rows?.filter((row) => {
        const q = searchQuery.toLowerCase();
        return (
          row.invoice_number.toLowerCase().includes(q) ||
          row.client_name.toLowerCase().includes(q) ||
          row.case_subject.toLowerCase().includes(q) ||
          row.status.toLowerCase().includes(q)
        );
      })
    : rows;

  const handleOpenDialog = (item?: ProformaInvoice) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        client_name: item.client_name,
        case_subject: item.case_subject,
        amount: item.amount.toString(),
        currency: item.currency,
        issue_date: item.issue_date,
        due_date: item.due_date,
        description: item.description,
      });
    } else {
      setSelectedItem(null);
      setFormData(initialFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (selectedItem) {
      setRows(rows?.map((r) => r.id === selectedItem.id ? { ...r, ...formData, amount: parseFloat(formData.amount) } : r));
    } else {
      const newItem: ProformaInvoice = {
        id: rows?.length + 1 || 1,
        invoice_number: `PRF-2026-${String(rows?.length + 1 || 1).padStart(3, '0')}`,
        ...formData,
        amount: parseFloat(formData.amount),
        status: 'draft',
      };
      setRows([...(rows || []), newItem]);
    }
    handleCloseDialog();
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm(t('billing.confirmDeleteProforma'));
    if (ok) {
      setRows(rows?.filter((r) => r.id !== id));
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' => {
    const map: Record<string, 'default' | 'primary' | 'success' | 'warning'> = { draft: 'default', sent: 'primary', approved: 'success', rejected: 'warning' };
    return map[status] || 'default';
  };

  const columns: GridColDef[] = [
    { field: 'invoice_number', headerName: t('billing.invoiceNumber').toUpperCase(), width: 150 },
    { field: 'client_name', headerName: t('billing.client').toUpperCase(), width: 160 },
    { field: 'case_subject', headerName: t('billing.case').toUpperCase(), width: 180 },
    { field: 'amount', headerName: t('billing.amount').toUpperCase(), width: 120, type: 'number', renderCell: (params: GridRenderCellParams) => `${params.row.currency} ${params.row.amount.toLocaleString()}` },
    { field: 'issue_date', headerName: t('billing.issueDate').toUpperCase(), width: 120 },
    { field: 'due_date', headerName: t('billing.dueDate').toUpperCase(), width: 120 },
    { field: 'status', headerName: t('billing.status').toUpperCase(), width: 120, renderCell: (params: GridRenderCellParams) => <Chip label={params.row.status} color={getStatusColor(params.row.status)} size="small" /> },
    {
      field: 'actions', headerName: t('common.actions').toUpperCase(), width: 180, renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('common.view')}><IconButton size="small"><ViewIcon /></IconButton></Tooltip>
          <Tooltip title={t('reports.exportPdf')}><IconButton size="small"><PdfIcon /></IconButton></Tooltip>
          <Tooltip title={t('common.edit')}><IconButton size="small" onClick={() => handleOpenDialog(params.row)}><EditIcon /></IconButton></Tooltip>
          <Tooltip title={t('common.delete')}><IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon /></IconButton></Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('billing.proformaInvoices')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>{t('billing.newProforma')}</Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <TextField fullWidth placeholder={t('billing.searchProformaInvoices')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }} sx={{ maxWidth: 600 }} />
        </Box>
      </Card>

      <Card>
        <DataGrid rows={filteredRows} columns={columns} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} pageSizeOptions={[10]} checkboxSelection disableRowSelectionOnClick autoHeight />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedItem ? t('billing.editProforma') : t('billing.newProformaInvoice')}
          <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('billing.client')} fullWidth value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} />
              <TextField label={t('billing.caseSubject')} fullWidth value={formData.case_subject} onChange={(e) => setFormData({ ...formData, case_subject: e.target.value })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('billing.amount')} type="number" fullWidth value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
              <TextField select label={t('cases.currency')} fullWidth value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })}>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('billing.issueDate')} type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} />
              <TextField label={t('billing.dueDate')} type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
            </Box>
            <TextField label={t('common.description')} fullWidth multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel').toUpperCase()}</Button>
          <Button onClick={handleSubmit} variant="contained">{selectedItem ? t('common.update').toUpperCase() : t('common.create').toUpperCase()}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Proforma;
